import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment-timezone";

import styles from "./StatusChange.css";

import Checkbox from "components/library/Checkbox";
import TakeoverPanel from "components/library/TakeoverPanel";
import ReadOnlyWrapper from "editor/containers/ReadOnlyWrapper";
import TakeoverHeader from "components/library/TakeoverHeader";
import Label from "components/library/Label";
import Button from "components/library/Button";
import DatePicker from "components/library/DatePicker";
import TimePicker from "components/library/TimePicker";
import DashboardStory from "dashboard/components/DashboardStory";
import Spinner from "components/library/Spinner";
import RadioGroup from "components/library/RadioGroup";
import RadioGroupOption from "components/library/RadioGroup/RadioGroupOption";
import Embargo from "editor/components/Embargo";
import { STATUS } from "config/constants";
import { URL_TO_API } from "config/constants/editor";
import { updateFormAt } from "editor/actions/form";
import {
  saveForm,
  setEditorType,
  publishFormAsync,
  scheduleFormAsync,
} from "editor/actions/status";
import { hydrateForm } from "editor/actions/form";
import { setMessage } from "actions/message";
import { sendNewsletterAsync } from "editor/actions/newsletter";
import { unlockItem } from "editor/actions/locking";
import { SECTIONS } from "config/constants";
import validateFutureDate from "editor/utils/validateFutureDate";
import isDateNotInPast from "editor/utils/isDateNotInPast";

import { DATETIME_FORMAT } from "config/constants";

class StatusChange extends React.Component {
  constructor(props) {
    super(props);
    this.statusChange = props.match.params.status;
    this.editorType = props.match.params.type;

    this.closeEditor = this._closeEditor.bind(this);
    this.schedule = this._schedule.bind(this);
    this.unschedule = this._unschedule.bind(this);
    this.publish = this._publish.bind(this);
    this.publishBreakingNews = this._publishBreakingNews.bind(this);
    this.unpublish = this._unpublish.bind(this);
    this.unfeature = this._unfeature.bind(this);
    this.send = this._send.bind(this);
    this.delete = this._delete.bind(this);
    this.update = this._update.bind(this);
    this.updatePublishTime = this._updatePublishTime.bind(this);
    this.catchError = this._catchError.bind(this);
    this.getDateComponent = this._getDateComponent.bind(this);
    this.getUpdateComponent = this._getUpdateComponent.bind(this);
    this.getStoryComponent = this._getStoryComponent.bind(this);
    this.handleEmbargoToggle = this._handleEmbargoToggle.bind(this);

    this.state = {
      datetime: props.form.scheduled_date
        ? props.form.scheduled_date
        : moment(),
      updatePublishTime: "false",
      wasClicked: false,
      isEmbargoToggled: false,
    };
  }

  UNSAFE_componentWillMount() {
    this.props.setEditorType(URL_TO_API[this.editorType]);
  }

  componentDidMount() {
    if (this.props.match.params.id && !this.props.match.params.revision) {
      this.props.hydrateForm(this.props.match.params.id);
    }
  }

  _closeEditor() {
    this.props.history.go(-1);
  }

  onChange(key) {
    return value => {
      this.setState({ [key]: value });
    };
  }

  _updatePublishTime(e) {
    this.setState({ updatePublishTime: e.target.value });
  }

  _catchError(error) {
    this.props.setMessage({ message: error });
    this.setState({ wasClicked: false });
  }

  _getStoryComponent() {
    if (!this.props.form.id) return null;
    let headline;
    try {
      headline =
        this.editorType === "newsletter"
          ? this.props.form.subscription.name
          : this.props.form.headline;
    } catch (e) {
      headline = "No headline";
    }

    // We store a few things in the form differently in the editor than how the API returns them
    // convert isTopStory so that it can show up in the storyComponent, we require a section
    let sections = this.props.form.sections || [];
    if (this.props.form.isTopStory) {
      sections = [SECTIONS.TOP, ...this.props.form.sections];
    }

    // DashboardStory requires additional props that are usually only available from dashboard api
    const story = {
      ...this.props.form,
      sections: sections,
      lock: {},
      editorType: this.editorType,
      headline,
    };

    if (this.props.isLoading) {
      return (
        <div className={styles.loading}>
          <Spinner />
        </div>
      );
    }

    return (
      <div className={styles.story}>
        <DashboardStory story={story} />
      </div>
    );
  }

  _getDateComponent() {
    return (
      <div>
        <Label className={styles.label} htmlFor="datetime">
          Publish at
        </Label>
        <div className={styles.datetime}>
          <DatePicker
            value={this.state.datetime}
            error={this.props.form.errors.scheduled_date}
            onChange={this.onChange("datetime")}
            isValidDate={isDateNotInPast}
          />
          <TimePicker
            value={this.state.datetime}
            error={this.props.form.errors.scheduled_date}
            onChange={this.onChange("datetime")}
          />
        </div>
        <div className={styles.embargo}>
          {isDateNotInPast(this.props.form.embargo_until, "minute") ? (
            <span>
              Note: <Embargo form={this.props.form} />.
            </span>
          ) : (
            <Checkbox
              label="Create embargo until scheduled time"
              onChange={this.handleEmbargoToggle}
              isChecked={this.state.isEmbargoToggled}
            />
          )}
        </div>
      </div>
    );
  }

  _handleEmbargoToggle() {
    this.setState(oldState => ({
      isEmbargoToggled: !oldState.isEmbargoToggled,
    }));
  }

  _getUpdateComponent() {
    return (
      <div className={styles.update}>
        <Label className={styles.label} htmlFor="publishTime">
          Publish time
        </Label>
        <RadioGroup
          onChange={this.updatePublishTime}
          selectedValue={this.state.updatePublishTime}
        >
          <RadioGroupOption
            value="false"
            label={`Keep the original publish time (${moment(
              this.props.form.published_date
            ).fromNow()})`}
          ></RadioGroupOption>
          <RadioGroupOption
            value="true"
            label="Update the publish time to now"
          ></RadioGroupOption>
        </RadioGroup>
      </div>
    );
  }

  validateEmbargoDate(embargo, datetime) {
    //if no error, this function returns undefined by design
    let message;
    let embargoTime = moment(embargo);
    let formattedEmbargoTime = embargoTime.format(DATETIME_FORMAT);
    if (datetime.isSameOrBefore(embargoTime)) {
      message =
        "This scheduled date conflicts with the embargo set for " +
        formattedEmbargoTime;
      return new Error(message);
    }
  }

  _schedule() {
    let error = validateFutureDate(this.state.datetime);
    error =
      error ||
      this.validateEmbargoDate(
        this.props.form.embargo_until,
        this.state.datetime
      );
    if (error) {
      this.props.updateFormAt("errors", { scheduled_date: error });
    } else {
      this.setState({ wasClicked: true });
      this.props.updateFormAt("scheduled_date", this.state.datetime);
      if (this.state.isEmbargoToggled) {
        this.props.updateFormAt("embargo_until", this.state.datetime);
      }
      return this.props
        .saveForm(true)
        .then(() => this.props.scheduleFormAsync(this.props.form.id))
        .then(() => {
          this.props.unlockItem(this.props.form.id);
          this.props.history.push("/dashboard/published");
          this.props.setMessage({
            type: "success",
            message: `Your ${this.editorType} has been scheduled.`,
          });
        })
        .catch(this.catchError);
    }
  }

  _unschedule() {
    this.setState({ wasClicked: true });
    this.props.updateFormAt("status", STATUS.READY_FOR_EDITS.slug);
    this.props.updateFormAt("scheduled_date", null);
    return this.props
      .saveForm(true)
      .then(() => {
        this.props.unlockItem(this.props.form.id);
        this.props.history.push("/dashboard");
        this.props.setMessage({
          message: `Your ${this.editorType} has been unscheduled.`,
        });
      })
      .catch(this.catchError);
  }

  _publish() {
    this.setState({ wasClicked: true });
    this.props.updateFormAt("published_date", new Date().toISOString());
    return this.props
      .saveForm(true)
      .then(() => this.props.publishFormAsync(this.props.form.id))
      .then(() => {
        this.props.unlockItem(this.props.form.id);
        this.props.history.push("/dashboard/published");
        this.props.setMessage({
          type: "success",
          message: `Your ${this.editorType} has been published.`,
        });
      })
      .catch(this.catchError);
  }

  _publishBreakingNews() {
    this.setState({ wasClicked: true });
    this.props.updateFormAt("is_breaking_news", true);
    return this.props
      .saveForm(true)
      .then(() => this.props.publishFormAsync(this.props.form.id))
      .then(() => {
        this.props.history.replace(
          `/editor/story/${this.props.form.id}/send-alert`
        );
        this.props.setMessage({
          type: "success",
          message: `Your ${this.editorType} has been published as breaking news.`,
        });
      })
      .catch(this.catchError);
  }

  _update() {
    this.setState({ wasClicked: true });
    if (this.state.updatePublishTime === "true") {
      this.props.updateFormAt("published_date", new Date().toISOString());
    }
    return this.props
      .saveForm(true)
      .then(() => this.props.publishFormAsync(this.props.form.id))
      .then(() => {
        this.props.unlockItem(this.props.form.id);
        this.props.history.push("/dashboard/published");
        this.props.setMessage({
          type: "success",
          message: `Your ${this.editorType} has been updated.`,
        });
      })
      .catch(this.catchError);
  }

  _unpublish() {
    this.setState({ wasClicked: true });
    this.props.updateFormAt("status", STATUS.IN_PROGRESS.slug);
    this.props.updateFormAt("scheduled_date", null);
    return this.props
      .saveForm(true)
      .then(() => {
        this.props.unlockItem(this.props.form.id);
        this.props.history.push("/dashboard");
        this.props.setMessage({
          message: `Your ${this.editorType} has been unpublished.`,
        });
      })
      .catch(this.catchError);
  }

  _unfeature() {
    this.setState({ wasClicked: true });
    this.props.updateFormAt("featured_sections", []);
    return this.props
      .saveForm(true)
      .then(() => {
        this.props.unlockItem(this.props.form.id);
        this.props.history.push("/dashboard");
        this.props.setMessage({
          message: `Your ${this.editorType} has been unfeatured.`,
        });
      })
      .catch(this.catchError);
  }

  _send() {
    this.setState({ wasClicked: true });
    return this.props
      .sendNewsletterAsync(this.props.form.id, true)
      .then(() => {
        this.props.unlockItem(this.props.form.id);
        this.props.history.push("/dashboard/published");
        this.props.setMessage({
          type: "success",
          message: "Your newsletter was sent.",
        });
      })
      .catch(this.catchError);
  }

  _delete() {
    this.setState({ wasClicked: true });
    this.props.updateFormAt("status", STATUS.DELETED.slug);
    return this.props
      .saveForm(true)
      .then(() => {
        this.props.unlockItem(this.props.form.id);
        this.props.history.push("/dashboard");
        this.props.setMessage({
          message: "Your draft has been deleted.",
        });
      })
      .catch(this.catchError);
  }

  renderInfo(statusChange) {
    const statusInfo = {
      schedule: {
        cancel: "Cancel",
        cta: "Schedule",
        ctaAria: `Schedule the specified ${this.editorType}`,
        header: `Schedule ${this.editorType}`,
        component: this._getDateComponent(),
        onClick: this.schedule,
      },
      unschedule: {
        cancel: "No, cancel",
        cta: "Yes, unschedule it",
        ctaAria: `Unschedule the specified ${this.editorType}`,
        header: `Are you sure you want to unschedule this ${this.editorType}?`,
        component: this._getStoryComponent(),
        onClick: this.unschedule,
      },
      publish: {
        cancel: "No, cancel",
        cta: "Yes, publish it",
        ctaAria: `Schedule the specified ${this.editorType}`,
        header: `Are you sure you want to publish this ${this.editorType}?`,
        component: this._getStoryComponent(),
        onClick: this.publish,
      },
      breaking: {
        cancel: "No, cancel",
        cta: "Yes, publish it as breaking news",
        ctaAria: `Publish the specified ${this.editorType} as breaking news`,
        header: `Are you sure you want to publish this ${this.editorType} as breaking news?`,
        component: this._getStoryComponent(),
        onClick: this.publishBreakingNews,
      },
      unpublish: {
        cancel: "No, cancel",
        cta: "Yes, unpublish it",
        ctaAria: `Unpublish the specified ${this.editorType}`,
        header: `Are you sure you want to unpublish this ${this.editorType}?`,
        component: this._getStoryComponent(),
        onClick: this.unpublish,
      },
      unfeature: {
        cancel: "No, cancel",
        cta: "Yes, unfeature it",
        ctaAria: `Unfeature the specified ${this.editorType}`,
        header: `Are you sure you want to unfeature this ${this.editorType}?`,
        component: this._getStoryComponent(),
        onClick: this.unfeature,
      },
      send: {
        cancel: "No, cancel",
        cta: "Yes, send it",
        ctaAria: "Send the specified newsletter",
        header: "Are you sure you want to send this newsletter?",
        component: this._getStoryComponent(),
        onClick: this.send,
      },
      delete: {
        cancel: "No, cancel",
        cta: "Yes, delete it",
        ctaAria: "Delete the specified draft",
        header: "Are you sure you want to delete this draft?",
        component: this._getStoryComponent(),
        onClick: this.delete,
      },
      update: {
        cancel: "Cancel",
        cta: "Update",
        ctaAria: `Update the specified ${this.editorType}`,
        header: `Update ${this.editorType}`,
        component: this._getUpdateComponent(),
        onClick: this.update,
      },
    };

    return statusInfo[statusChange];
  }

  render() {
    const statusInfo = this.renderInfo(this.statusChange);

    return (
      <ReadOnlyWrapper
        id={this.props.match.params.id}
        status={this.props.form.status}
      >
        <TakeoverPanel
          containerStyle={styles.container}
          className={styles.takeoverPanel}
        >
          <TakeoverHeader>{statusInfo.header}</TakeoverHeader>

          {statusInfo.component}
          {/* TODO: Create library component for button group. */}
          <div className={styles.buttonGroup}>
            <Button
              onClick={statusInfo.onClick}
              type="submit"
              data-cy="onChangeStoryButton"
              aria-label={statusInfo.ctaAria}
              isDisabled={this.state.wasClicked || this.props.isReadOnly}
            >
              {statusInfo.cta}
            </Button>

            <Button
              role="link"
              onClick={this.closeEditor}
              type="cancel"
              aria-label="Cancel status change and go back to the previous page"
              isDisabled={this.props.isReadOnly}
            >
              {this.state.wasClicked ? "Close" : statusInfo.cancel}
            </Button>
          </div>
        </TakeoverPanel>
      </ReadOnlyWrapper>
    );
  }
}

const mapStateToProps = state => ({
  form: state.editor.form,
  isLoading: state.editor.ui.isLoading,
  isReadOnly: state.editor.status.isReadOnly,
});

const mapDispatchToProps = {
  saveForm,
  publishFormAsync,
  scheduleFormAsync,
  setEditorType,
  updateFormAt,
  hydrateForm,
  setMessage,
  sendNewsletterAsync,
  unlockItem,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StatusChange);

StatusChange.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  hydrateForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  publishFormAsync: PropTypes.func.isRequired,
  scheduleFormAsync: PropTypes.func.isRequired,
  updateFormAt: PropTypes.func.isRequired,
  setMessage: PropTypes.func.isRequired,
  setEditorType: PropTypes.func.isRequired,
  unlockItem: PropTypes.func.isRequired,
  sendNewsletterAsync: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isReadOnly: PropTypes.bool,
};
